import React, { useCallback, useMemo, useState } from 'react';
import { FiAlignLeft, FiCheck, FiCopy, FiMaximize2, FiMinimize2 } from 'react-icons/fi';
import { successAlert } from '@components/toastify/notify-toast';
import styles from '@pages/api-portal/styles.module.scss';
import { JSON_TOKEN_COLORS, tokenizeJsonLine } from '@pages/api-portal/utils';

interface BodyCodeBlockProps {
    label: string;
    data: Record<string, unknown> | undefined;
}

const COPY_CONFIRMATION_MS = 2000;

/*
 * Syntax-highlighted, line-numbered JSON viewer with copy, pretty-print, and expand/collapse controls.
 */
export function BodyCodeBlock({ label, data }: BodyCodeBlockProps) {
    const [copied, setCopied] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [formatted, setFormatted] = useState(true);

    /*
     * Serialized JSON — pretty-printed or minified based on the format toggle.
     */
    const json = useMemo(
        () => (formatted ? JSON.stringify(data ?? {}, null, 2) : JSON.stringify(data ?? {})),
        [formatted, data]
    );
    const lines = useMemo(() => json.split('\n'), [json]);

    /*
     * Copies the current JSON string to clipboard and shows a brief confirmation tick.
     */
    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(json);
            successAlert('Copied to clipboard.');
            setCopied(true);
            setTimeout(() => setCopied(false), COPY_CONFIRMATION_MS);
        } catch {
            // Clipboard API unavailable — nothing further to do.
        }
    }, [json]);

    /*
     * Toggles between pretty-printed and minified JSON output.
     */
    const handleFormat = useCallback(() => setFormatted((prev) => !prev), []);

    /*
     * Toggles whether all lines are visible or capped at the default max height.
     */
    const handleExpand = useCallback(() => setExpanded((prev) => !prev), []);

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span className={styles.mutedText} style={{ fontSize: 12 }}>
                    {label}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <button type="button" className={styles.iconTextButton} onClick={handleFormat} title="Format">
                        <FiAlignLeft size={12} />
                        Format
                    </button>
                    <button
                        type="button"
                        className={styles.iconTextButton}
                        onClick={handleExpand}
                        title={expanded ? 'Collapse' : 'Expand'}
                    >
                        {expanded ? <FiMinimize2 size={12} /> : <FiMaximize2 size={12} />}
                        {expanded ? 'Collapse' : 'Expand'}
                    </button>
                    <button type="button" className={styles.iconButton} onClick={handleCopy} title="Copy">
                        {copied ? <FiCheck size={12} /> : <FiCopy size={12} />}
                    </button>
                </div>
            </div>
            <div className={styles.codeTableWrapper}>
                <div className={expanded ? undefined : styles.codeTableBodyCapped}>
                    <table className={styles.codeTable}>
                        <tbody>
                            {lines.map((line, i) => (
                                <tr key={i}>
                                    <td className={styles.lineNumberCell}>{String(i + 1).padStart(2, '0')}</td>
                                    <td className={styles.lineContentCell}>
                                        {tokenizeJsonLine(line).map((token, j) => (
                                            <span key={j} style={{ color: JSON_TOKEN_COLORS[token.type] }}>
                                                {token.text}
                                            </span>
                                        ))}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
